import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  Nav,
  OverlayTrigger,
  Popover,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Pencil, Rocket, Search } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { ECRImage } from '@/types';
import { getEcrService } from '@/services/ecrService';
import { setImageMetadata, type RepositoryName } from '@/services/imageTagService';
import { LIVE_LOOKBACK_DAYS, liveImageKey, loadLiveImages } from '@/services/fleetImageService';
import { PageHeader } from '@/components/ui/PageHeader';
import { CopyButton } from '@/components/ui/CopyButton';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import './Containers.css';

type Channel = RepositoryName;

const CHANNELS: { id: Channel; label: string; description: string }[] = [
  { id: 'numa-deploy', label: 'Production', description: 'Built automatically on every merge to main.' },
  { id: 'numa-deploy-dev', label: 'Dev', description: 'Built by hand from dev or a dev-image branch.' },
];

const shortDigest = (digest: string) => {
  const hex = digest.replace(/^sha256:/, '');
  return hex.length > 16 ? `${hex.slice(0, 8)}…${hex.slice(-6)}` : hex;
};

const formatSize = (sizeMb: number) => (sizeMb > 1024 ? `${(sizeMb / 1024).toFixed(1)} GB` : `${sizeMb} MB`);

export default function Containers() {
  const [channel, setChannel] = useState<Channel>('numa-deploy');
  const [imagesByChannel, setImagesByChannel] = useState<Record<Channel, ECRImage[]>>({
    'numa-deploy': [],
    'numa-deploy-dev': [],
  });
  const [loadingByChannel, setLoadingByChannel] = useState<Record<Channel, boolean>>({
    'numa-deploy': true,
    'numa-deploy-dev': true,
  });
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [liveClients, setLiveClients] = useState<Map<string, string[]> | null>(null);
  const [editingImage, setEditingImage] = useState<ECRImage | null>(null);
  const [customName, setCustomName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadChannel = async (target: Channel) => {
    setLoadingByChannel((prev) => ({ ...prev, [target]: true }));
    setError(null);
    try {
      const data = await getEcrService(target).getAllImages();
      setImagesByChannel((prev) => ({ ...prev, [target]: data }));
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to load ${target} images`);
    } finally {
      setLoadingByChannel((prev) => ({ ...prev, [target]: false }));
    }
  };

  // Eagerly load both channels so tab switches feel instant
  useEffect(() => {
    loadChannel('numa-deploy');
    loadChannel('numa-deploy-dev');
    // The client counts are a second, slower read. The table does not wait for them.
    loadLiveImages()
      .then((live) => setLiveClients(live.clientsByImage))
      .catch((e) => {
        console.error('Failed to load which clients run each image', e);
        setLiveClients(new Map());
      });
  }, []);

  const images = imagesByChannel[channel];
  const loading = loadingByChannel[channel];
  const activeChannel = CHANNELS.find((c) => c.id === channel)!;

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return images;
    return images.filter(
      (img) =>
        img.tag.toLowerCase().includes(term) ||
        img.digest.toLowerCase().includes(term) ||
        (img.customName || '').toLowerCase().includes(term) ||
        (img.description || '').toLowerCase().includes(term)
    );
  }, [search, images]);

  const handleEditImage = (img: ECRImage) => {
    setEditingImage(img);
    setCustomName(img.customName || '');
    setDescription(img.description || '');
  };

  const handleSaveMetadata = async () => {
    if (!editingImage) return;
    setIsSaving(true);
    try {
      await setImageMetadata({
        repository: channel,
        imageTag: editingImage.tag,
        digest: editingImage.digest,
        customName: customName.trim() || undefined,
        description: description.trim() || undefined,
      });

      getEcrService(channel).clearCache();
      await loadChannel(channel);
      setEditingImage(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setEditingImage(null);
    setCustomName('');
    setDescription('');
  };

  const renderLive = (img: ECRImage) => {
    if (!liveClients) return <Spinner animation="border" size="sm" className="images-live-spinner" />;
    const clients = liveClients.get(liveImageKey(img.repository, img.tag)) || [];
    if (clients.length === 0) return <span className="text-muted">None</span>;
    return (
      <OverlayTrigger
        trigger="click"
        rootClose
        placement="bottom-start"
        overlay={
          <Popover className="images-live-popover">
            <Popover.Header>
              Live on {clients.length} {clients.length === 1 ? 'client' : 'clients'}
            </Popover.Header>
            <Popover.Body>
              <ul className="images-live-list">
                {clients.map((client) => (
                  <li key={client}>
                    <Link to={`/deployments?client=${encodeURIComponent(client)}`}>{client}</Link>
                  </li>
                ))}
              </ul>
              <p className="images-live-note">
                A client counts here when its newest successful deploy in the last {LIVE_LOOKBACK_DAYS} days used this
                image.
              </p>
            </Popover.Body>
          </Popover>
        }
      >
        <button type="button" className="images-live">
          {clients.length} {clients.length === 1 ? 'client' : 'clients'}
        </button>
      </OverlayTrigger>
    );
  };

  return (
    <div className="images-page">
      <PageHeader title="Container images" />

      <div className="images-toolbar">
        <Nav variant="tabs" activeKey={channel} onSelect={(k) => k && setChannel(k as Channel)}>
          {CHANNELS.map((c) => (
            <Nav.Item key={c.id}>
              <Nav.Link eventKey={c.id}>
                {c.label}
                <span className="images-tab-count">{imagesByChannel[c.id].length}</span>
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <InputGroup className="images-search">
          <InputGroup.Text>
            <Search />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search by name, tag or digest"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search images"
          />
        </InputGroup>
      </div>

      <p className="images-channel-note">
        {activeChannel.description}{' '}
        {!loading && (
          <span>
            {search.trim()
              ? `${filtered.length} of ${images.length} match.`
              : `${images.length} ${images.length === 1 ? 'image' : 'images'}.`}
          </span>
        )}
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Table hover responsive className="mb-0 images-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Pushed</th>
              <th className="text-end">Size</th>
              <th>Live on</th>
              <th>Digest</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="images-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading images
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((img, index) => (
                <tr key={img.digest}>
                  <td>
                    <div className="images-name">
                      {img.customName ? (
                        <span className="images-name__title">{img.customName}</span>
                      ) : (
                        <>
                          <span className="images-name__title mono">{img.tag}</span>
                          <CopyButton value={img.tag} label="Copy tag" />
                        </>
                      )}
                      {index === 0 && !search.trim() && <Badge bg="primary">Latest</Badge>}
                    </div>
                    {(img.customName || img.description) && (
                      <div className="images-name__meta">
                        {img.customName && (
                          <>
                            <span className="mono">{img.tag}</span>
                            <CopyButton value={img.tag} label="Copy tag" />
                          </>
                        )}
                        {img.description && <span className="images-name__desc">{img.description}</span>}
                      </div>
                    )}
                  </td>
                  <td>
                    <div>{timeAgo(img.pushedAt)}</div>
                    <div className="images-sub">{formatNzDateTime(img.pushedAt)}</div>
                  </td>
                  <td className="text-end numeric">{formatSize(img.sizeMb)}</td>
                  <td>{renderLive(img)}</td>
                  <td>
                    <span className="mono" title={img.digest}>
                      {shortDigest(img.digest)}
                    </span>
                    <CopyButton value={img.digest} label="Copy full digest" />
                  </td>
                  <td className="images-actions">
                    <Button size="sm" variant="outline-secondary" onClick={() => handleEditImage(img)}>
                      <Pencil size={12} className="me-1" />
                      {img.customName ? 'Rename' : 'Name'}
                    </Button>
                    <Link
                      className="btn btn-sm btn-outline-primary"
                      to={`/deployments?channel=${encodeURIComponent(channel)}&image=${encodeURIComponent(img.tag)}`}
                    >
                      <Rocket size={12} className="me-1" />
                      Deploy
                    </Link>
                  </td>
                </tr>
              ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="images-empty">
                  {search.trim() ? 'No image matches that search.' : 'No images in this channel yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Modal show={!!editingImage} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingImage?.customName ? 'Rename image' : 'Name image'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingImage && (
            <>
              <p className="text-muted mb-3">
                {activeChannel.label} image <span className="mono">{editingImage.tag}</span>, pushed{' '}
                {formatNzDateTime(editingImage.pushedAt)}.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Numa 2.95 Release"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  autoFocus
                />
                <Form.Text>Shown in place of the tag wherever an image is picked. Leave empty to remove it.</Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Note (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="What this image contains, or who it is for"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveMetadata} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
